export type LibraryLocation = {
  view?: "level" | "topic" | "grammar";
  theme?: string;
  grammar?: string;
  level?: string;
  q?: string;
};

function withQuery(path: string, values: Record<string, string | undefined>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value) query.set(key, value);
  }
  return query.size ? `${path}?${query}` : path;
}

// Typed href builders — the only place route paths are written as strings.
export const routes = {
  content: () => "/content",
  design: () => "/design",
  levels: () => "/levels",
  studyLibrary: (selection: LibraryLocation = {}) =>
    withQuery("/levels", selection),
  system: () => "/system",
  systemOverview: () => "/system/overview",
  systemConcept: (conceptId: string) => `/system/${conceptId}`,
  level: (levelId: string, context: LibraryLocation = {}) =>
    withQuery(`/levels/${levelId}`, context),
  unit: (
    level: number,
    unitId: string,
    context: LibraryLocation = {},
    selection: { stage?: string; cycle?: string } = {},
  ) =>
    withQuery(`/levels/level-${level}/chapters/${encodeURIComponent(unitId)}`, {
      ...context,
      ...selection,
    }),
  chapter: (
    level: number,
    chapterId: string,
    context: LibraryLocation = {},
    sectionUid?: string,
  ) =>
    withQuery(
      `/levels/level-${level}/chapters/${encodeURIComponent(chapterId)}`,
      { ...context, section: sectionUid },
    ),
  section: (level: number, uid: string, context: LibraryLocation = {}) =>
    withQuery(
      `/levels/level-${level}/sections/${encodeURIComponent(uid)}`,
      context,
    ),
  levelGuide: (levelId: string) => `/levels/${levelId}/guide`,
  levelGuideConcept: (levelId: string, conceptId: string) =>
    `/levels/${levelId}/guide/${conceptId}`,
  levelSupport: (levelId: string) => `/levels/${levelId}/support`,
  levelSupportConcept: (levelId: string, conceptId: string) =>
    `/levels/${levelId}/support/${conceptId}`,
  topic: (
    levelId: string,
    topicId: string,
    context: LibraryLocation = {},
    sectionUid?: string,
  ) =>
    withQuery(`/levels/${levelId}/topics/${topicId}`, {
      libraryView: context.view,
      libraryTheme: context.theme,
      libraryGrammar: context.grammar,
      libraryLevel: context.level,
      librarySearch: context.q,
      librarySection: sectionUid,
    }),
} as const;
